import React from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { TextAreaController } from '@components-clean/core/text-area';
import { ControlsReviewLog } from '@interfaces-clean/review-logs.interface';
import styles from '@pages-clean/admin/study/admin-data-views/data-views/details/style.module.scss';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError } from '@utils/notification';
import ControlsReviewLogsApi from '@apis/bff-api/central-monitoring/controls-review-log';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';

export interface ReviewLogDeleteDialogProps {
    recordId: string;
    onClose: () => void;
    studyRef: string;
    onDeleteHandler: (success: boolean | undefined) => void;
    reviewLogId: number;
    observationId: string | null | undefined;
    reviewLogDatasetDate: string | null | undefined;
}

export type FormInputs = {
    deleteReason: string | null;
};

const DeleteControlsReviewLogDialog = ({
    recordId,
    onClose,
    studyRef,
    onDeleteHandler,
    reviewLogId,
    observationId,
    reviewLogDatasetDate,
}: ReviewLogDeleteDialogProps) => {
    const { t } = useTranslation('cm');

    const { trigger: deleteControlReviewLog } = ControlsReviewLogsApi.useDelete(studyRef);
    const { data: review, isLoading } = ControlsReviewLogsApi.useGet(studyRef, `${reviewLogId}`);

    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const { data: observationDTO } = ObservationsApi.useGet(studyRef, observationId || '');

    const onDelete = async (data: FormInputs) => {
        const deleteHandler = deleteControlReviewLog as (args: {
            deletedReason: string | null;
            reviewLogId: number;
            observationId?: string | null;
            datasetDate?: string | null;
        }) => Promise<boolean>;

        try {
            const response: boolean = await deleteHandler({
                deletedReason: data.deleteReason,
                reviewLogId,
                observationId,
                datasetDate: reviewLogDatasetDate,
            });
            onDeleteHandler(response);
        } catch {
            ShowError(t('common:errorMessage'));
        }
    };

    const maxChars = 350;

    // We need to append the additional height to the dialog depending on the observation length to avoid scroll bar.
    // We need to show a maximum of 5 lines for observation text which is approx 350 chars. Calculating the no of lines
    // and then appending 15 px height per line. 270 is the minimum required length. 65 px is if the number of lines is more than 5.
    const minDialogHeight = 370;
    const allowedAdditionalMaxHeight = 65;
    const approxCharsPerLine = 70;

    const calculatedHeight =
        (observationDTO?.observation?.length as unknown as number) > maxChars
            ? allowedAdditionalMaxHeight
            : ((observationDTO?.observation?.length as unknown as number) / approxCharsPerLine) * 15;

    const reviewLog = review?.data && (review.data[0] as ControlsReviewLog);

    const warningText = (
        <span className={cn('k-w-full k-d-flex k-flex-column k-gap-6', styles.fontSize)}>
            <div>
                <span className="k-font-medium k-color-primary">{t('cm:reviewLogGrid.deleteReviewLogWarning1')} </span>
                <span className="k-font-medium k-color-secondary">
                    {stringDateWithoutTimeFromUtc(reviewLogDatasetDate, 'DD-MMM-YYYY')}
                </span>
            </div>
            <div className={style.deleteWarningOverflow}>
                <span className="k-font-medium">{t('cm:reviewLogGrid.deleteReviewLogWarning2')}</span>{' '}
                <span className={cn(style.textHighLightNoBold, style.observation)}>
                    {observationDTO && observationDTO.observation}
                </span>{' '}
            </div>
            <div>
                <span>{t('common:and')}</span>{' '}
                <span className="k-font-medium k-color-secondary">{(reviewLog && reviewLog.actions_count) || 0}</span>{' '}
                <span className="k-font-medium">{t('cm:reviewLogGrid.deleteReviewLogWarning3')}</span>
            </div>
        </span>
    );

    return (
        observationDTO && (
            <DraggableDialog
                dataTestId="review-log-delete-dialog"
                onSubmit={() => handleSubmit(onDelete)()}
                onClose={onClose}
                title={`${t('cm:reviewLogGrid.deleteControlsReviewLog')}: ${recordId}`}
                initialHeight={minDialogHeight + calculatedHeight}
                initialWidth={520}
                primaryBtnLabel={t('common:delete')}
                primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
                secondaryBtnDisabled={isSubmitting}
            >
                {isLoading ? (
                    <Loading />
                ) : (
                    <div className={cn('k-h-full k-w-full k-d-flex k-flex-column k-gap-2', style.controls)}>
                        <InfoField type="Warning" message={warningText} />
                        <TextAreaController
                            className={cn(style.textAreaOverrides)}
                            maxLength={3000}
                            control={control}
                            name="deleteReason"
                            required
                            data-testid="delete-review-log"
                            label={`${t('cm:deleteReasonLabel')} *`}
                            readOnly={isSubmitting}
                        />
                    </div>
                )}
            </DraggableDialog>
        )
    );
};

export default DeleteControlsReviewLogDialog;
